import { useEffect, useState, useRef } from "react";

// AuthenticatedImage component for handling private images
const AuthenticatedImage = ({ src, alt, className, fallback }) => {
  const [imageUrl, setImageUrl] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const mountedRef = useRef(true);
  const blobUrlRef = useRef(null); // Track blob URLs separately

  useEffect(() => {
    mountedRef.current = true;

    const loadImage = async () => {
      if (!src) {
        if (mountedRef.current) {
          setIsLoading(false);
          setError(true);
        }
        return;
      }

      // Check if the image is private (from your API domain)
      const isPrivateImage = src.includes("/api/images/private/");

      if (isPrivateImage) {
        try {
          const token = sessionStorage.getItem("ecoToken");

          // If no token, show fallback
          if (!token) {
            if (mountedRef.current) {
              setError(true);
              setIsLoading(false);
            }
            return;
          }

          const response = await fetch(src, {
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "image/*",
            },
          });

          if (response.ok) {
            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);
            blobUrlRef.current = objectUrl; // Store blob URL for cleanup
            if (mountedRef.current) {
              setImageUrl(objectUrl);
              setError(false);
              setIsLoading(false);
            }
          } else {
            if (mountedRef.current) {
              setError(true);
              setIsLoading(false);
            }
          }
        } catch (error) {
          console.error("Error loading authenticated image:", error);
          if (mountedRef.current) {
            setError(true);
            setIsLoading(false);
          }
        }
      } else {
        // Public image - just use the src directly
        if (mountedRef.current) {
          setImageUrl(src);
          setIsLoading(false);
          setError(false);
        }
      }
    };

    loadImage();

    // Cleanup function - only revoke blob URLs
    return () => {
      mountedRef.current = false;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [src]);

  // Show loading state
  if (isLoading) {
    return (
      <div
        className={`animate-pulse bg-gray-200 rounded ${className || ""}`}
        style={{
          width: className?.includes("w-[") ? "" : "170px",
          height: className?.includes("h-[") ? "" : "170px",
        }}
      />
    );
  }

  // Show error or fallback
  if (error || !imageUrl) {
    if (fallback) {
      return fallback;
    }

    return (
      <div
        className={`bg-gray-200 rounded flex items-center justify-center ${
          className || ""
        }`}
        style={{
          width: className?.includes("w-[") ? "" : "170px",
          height: className?.includes("h-[") ? "" : "170px",
        }}
      >
        <span className="text-gray-500 text-sm">
          {alt?.charAt(0)?.toUpperCase() || "?"}
        </span>
      </div>
    );
  }

  // Show the image
  return (
    <img
      src={imageUrl}
      alt={alt}
      className={className}
      onError={() => {
        if (mountedRef.current) {
          setError(true);
        }
      }}
      onLoad={() => {
        if (mountedRef.current) {
          setError(false);
        }
      }}
    />
  );
};

export default AuthenticatedImage;
